/**
 * useSessionTimeout — auto-logout dopo 30 minuti di inattività.
 *
 * Listener di attività (click, keydown, mousemove, scroll, touchstart)
 * resettano i timer. Throttled a 1s per non scrivere stato a ogni mousemove.
 *
 * - SESSION_WARNING_MS (default 28 min): chiama `onWarn` per aprire la
 *   modale "Stai per essere disconnesso".
 * - SESSION_IDLE_MS (default 30 min): chiama `onTimeout` (firma:
 *   signOut + redirect /login?reason=idle).
 *
 * Da montare DENTRO il layout protetto: non lo vogliamo attivo su
 * /login e /forgot-password.
 */

import { useEffect, useRef } from 'react';
import {
  SESSION_IDLE_MS,
  SESSION_WARNING_MS,
} from '../security/constants';

const ACTIVITY_EVENTS = [
  'click',
  'keydown',
  'mousemove',
  'scroll',
  'touchstart',
] as const;

const ACTIVITY_THROTTLE_MS = 1000;

export interface UseSessionTimeoutParams {
  enabled: boolean;
  onWarn: () => void;
  onTimeout: () => void;
  /** Override per test */
  warnMs?: number;
  /** Override per test */
  idleMs?: number;
}

export interface UseSessionTimeoutReturn {
  /** Resetta manualmente i timer (es. dopo "Rimani connesso"). */
  reset: () => void;
}

export function useSessionTimeout({
  enabled,
  onWarn,
  onTimeout,
  warnMs = SESSION_WARNING_MS,
  idleMs = SESSION_IDLE_MS,
}: UseSessionTimeoutParams): UseSessionTimeoutReturn {
  const warnTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 0 = "mai" → la prima attività utente passa sempre il throttle.
  const lastActivity = useRef<number>(0);
  const onWarnRef = useRef(onWarn);
  const onTimeoutRef = useRef(onTimeout);
  // "Latest ref" pattern: i callback aggiornano i ref ad ogni render
  // così i timer interni leggono sempre l'ultima callback senza dover
  // resettarsi (sarebbe troppo costoso, vedi listener attività).
  /* eslint-disable react-hooks/refs */
  onWarnRef.current = onWarn;
  onTimeoutRef.current = onTimeout;
  /* eslint-enable react-hooks/refs */

  const clear = () => {
    if (warnTimer.current) clearTimeout(warnTimer.current);
    if (timeoutTimer.current) clearTimeout(timeoutTimer.current);
    warnTimer.current = null;
    timeoutTimer.current = null;
  };

  const schedule = () => {
    clear();
    warnTimer.current = setTimeout(() => onWarnRef.current(), warnMs);
    timeoutTimer.current = setTimeout(
      () => onTimeoutRef.current(),
      idleMs
    );
  };

  useEffect(() => {
    if (!enabled) {
      clear();
      return;
    }
    schedule();
    const onActivity = () => {
      const now = Date.now();
      if (now - lastActivity.current < ACTIVITY_THROTTLE_MS) return;
      lastActivity.current = now;
      schedule();
    };
    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, onActivity, { passive: true });
    }
    return () => {
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, onActivity);
      }
      clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, warnMs, idleMs]);

  return {
    reset: () => {
      lastActivity.current = Date.now();
      schedule();
    },
  };
}
